import { useState } from "react";
import { Table, Button, Modal, Form, Select, Switch, Input, InputNumber, Space, Tag, Popconfirm, message } from "antd";
import { PlusOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import {
  useGetTaskStatusMappingsQuery,
  useCreateTaskStatusMappingMutation,
  useUpdateTaskStatusMappingMutation,
  useDeleteTaskStatusMappingMutation,
} from "../../../redux/api/scheduling";

const FIELD_TYPES = [
  { value: "status", label: "Production Status Field" },
  { value: "percentage", label: "Delivery Percentage" },
  { value: "datetime", label: "Date Reached" },
];

const OLI_STATUS_FIELDS = [
  { value: "ballooning_status", label: "Ballooning" },
  { value: "blank_fai_form_creation_status", label: "FAI Form" },
  { value: "keyence_programming_status", label: "Keyence Programming" },
  { value: "programming_template_status", label: "Programming Template" },
  { value: "material_selection_status", label: "Material Selection" },
  { value: "material_ordering_status", label: "Material Ordering" },
  { value: "material_prep_status", label: "Material Prep" },
  { value: "tooling_selection_status", label: "Tooling Selection" },
  { value: "tooling_ordering_status", label: "Tooling Ordering" },
  { value: "cam_programming_status", label: "CAM Programming" },
  { value: "cart_assembly_status", label: "Cart Assembly / Kitting" },
  { value: "machine_setup_status", label: "Machine Setup" },
  { value: "machining_status", label: "Machining" },
  { value: "quality_status", label: "Quality" },
  { value: "delivered_quantity", label: "Delivered Quantity (for %)" },
  { value: "material_received_date", label: "Material Received Date" },
  { value: "tooling_received_date", label: "Tooling Received Date" },
  { value: "qc_tooling_received_date", label: "QC Tooling Received Date" },
];

const TRIGGER_VALUES = [
  { value: "Not Started", label: "Not Started" },
  { value: "In Progress", label: "In Progress" },
  { value: "Completed", label: "Completed" },
];

const TARGET_STATUSES = [
  { value: "pending", label: "Pending" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
];

interface Props {
  taskId: number;
  taskName: string;
}

export default function TaskStatusMappings({ taskId, taskName }: Props) {
  const { data, isLoading, refetch } = useGetTaskStatusMappingsQuery({ task: taskId });
  const [createMapping] = useCreateTaskStatusMappingMutation();
  const [updateMapping] = useUpdateTaskStatusMappingMutation();
  const [deleteMapping] = useDeleteTaskStatusMappingMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form] = Form.useForm();
  const fieldType = Form.useWatch("field_type", form);

  const mappings = data?.results || data || [];

  const handleSubmit = async (values: any) => {
    try {
      if (editingId) {
        await updateMapping({ id: editingId, ...values }).unwrap();
        message.success("Mapping updated");
      } else {
        await createMapping({ task: taskId, ...values }).unwrap();
        message.success("Mapping created");
      }
      setModalOpen(false);
      form.resetFields();
      setEditingId(null);
      refetch();
    } catch (err: any) {
      message.error(err?.data?.detail || "Error saving mapping");
    }
  };

  const handleEdit = (record: any) => {
    setEditingId(record.id);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteMapping(id).unwrap();
      message.success("Mapping deleted");
      refetch();
    } catch (err: any) {
      message.error(err?.data?.detail || "Error deleting mapping");
    }
  };

  const columns = [
    {
      title: "Type",
      dataIndex: "field_type",
      key: "field_type",
      width: 120,
      render: (v: string) => <Tag color={v === "status" ? "blue" : v === "percentage" ? "orange" : "green"}>{v}</Tag>,
    },
    {
      title: "OLI Field",
      dataIndex: "oli_field",
      key: "oli_field",
      render: (v: string) => OLI_STATUS_FIELDS.find(f => f.value === v)?.label || v || "—",
    },
    {
      title: "Trigger",
      dataIndex: "trigger_value",
      key: "trigger_value",
      render: (v: string) => v || "—",
    },
    {
      title: "→ Target Status",
      dataIndex: "target_status",
      key: "target_status",
      render: (v: string) => {
        const color = v === "completed" ? "green" : v === "in_progress" ? "blue" : "default";
        return <Tag color={color}>{v}</Tag>;
      },
    },
    {
      title: "% Complete",
      dataIndex: "sets_percent_complete",
      key: "sets_percent_complete",
      width: 90,
      render: (v: boolean) => v ? <Tag color="orange">Yes</Tag> : "No",
    },
    {
      title: "AND Group",
      dataIndex: "logic_group",
      key: "logic_group",
      width: 100,
      render: (v: string) => v ? <Tag color="purple">{v}</Tag> : "—",
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 70,
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_: any, record: any) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => handleEdit(record)} />
          <Popconfirm title="Delete?" onConfirm={() => handleDelete(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold m-0">Status Mappings: {taskName}</h2>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => { setEditingId(null); form.resetFields(); setModalOpen(true); }}
        >
          Add Mapping
        </Button>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        These rules determine how OLI production statuses map to this task's JobTask status.
        Read-only from ERP — scheduling never writes to OLI fields.
      </p>

      <Table
        dataSource={mappings}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        size="small"
        pagination={false}
      />

      <Modal
        title={editingId ? "Edit Status Mapping" : "Add Status Mapping"}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingId(null); }}
        onOk={() => form.submit()}
        width={500}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="field_type" label="Field Type" rules={[{ required: true }]} initialValue="status">
            <Select options={FIELD_TYPES} />
          </Form.Item>
          <Form.Item name="oli_field" label="OLI Field" rules={[{ required: true }]}>
            <Select
              options={OLI_STATUS_FIELDS}
              showSearch
              filterOption={(input, option) =>
                (option?.label?.toString() || "").toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>
          {fieldType === "status" && (
            <Form.Item name="trigger_value" label="Trigger Value" rules={[{ required: true }]}>
              <Select options={TRIGGER_VALUES} />
            </Form.Item>
          )}
          <Form.Item name="target_status" label="Target JobTask Status" rules={[{ required: true }]}>
            <Select options={TARGET_STATUSES} />
          </Form.Item>
          <Form.Item name="sets_percent_complete" label="Sets % Complete" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="logic_group" label="AND Logic Group" tooltip="Rules with the same group name must ALL match for the target status to apply.">
            <Input placeholder="e.g. mc (leave empty for OR logic)" />
          </Form.Item>
          <Form.Item name="priority" label="Priority" initialValue={10} tooltip="Higher priority rules are evaluated first.">
            <InputNumber min={0} max={100} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
